import { CreditPackPurchaseRefusal, CreditUsageLevel } from '@insio/shared-types';

/** What the billing card offers a billing admin whose credits run low or out. */
export const CreditCallToActionKind = {
  NONE: 'NONE',
  BUY_PACK: 'BUY_PACK',
  UPGRADE: 'UPGRADE',
  /** Packs are shown, but may not be bought now; the refusal says why. */
  PACKS_DISABLED: 'PACKS_DISABLED',
  BILLING_PORTAL: 'BILLING_PORTAL'
} as const;

export type CreditCallToActionKind =
  (typeof CreditCallToActionKind)[keyof typeof CreditCallToActionKind];

export interface CreditCallToAction {
  kind: CreditCallToActionKind;
  /** Why no pack may be bought; `null` when one may, or when the offer is unknown. */
  refusal: CreditPackPurchaseRefusal | null;
}

interface CreditPackOfferView {
  purchasable: boolean;
  refusal?: CreditPackPurchaseRefusal | null;
}

export interface CreditCallToActionInput {
  level: CreditUsageLevel;
  /** `undefined` while unanswered; `null` when the payer has no offer. */
  offer: CreditPackOfferView | null | undefined;
  /** Whether a plan to move to is on the page. */
  canUpgrade: boolean;
  /** Whether the payer has a billing portal to fix its payment in. */
  canOpenBillingPortal: boolean;
}

const NONE: CreditCallToAction = { kind: CreditCallToActionKind.NONE, refusal: null };

/** The call to action from `LOW`: a pack when the offer allows it, otherwise per the refusal. */
export function creditCallToAction({
  level,
  offer,
  canUpgrade,
  canOpenBillingPortal
}: CreditCallToActionInput): CreditCallToAction {
  if (level === CreditUsageLevel.OK || offer === undefined) return NONE;
  if (offer?.purchasable) return { kind: CreditCallToActionKind.BUY_PACK, refusal: null };

  const refusal = offer?.refusal ?? null;
  const upgrade: CreditCallToAction = {
    kind: canUpgrade ? CreditCallToActionKind.UPGRADE : CreditCallToActionKind.NONE,
    refusal
  };

  switch (refusal) {
    case CreditPackPurchaseRefusal.MANUAL_PLAN:
      return { kind: CreditCallToActionKind.NONE, refusal };
    case CreditPackPurchaseRefusal.CHANGE_SCHEDULED:
      return { kind: CreditCallToActionKind.PACKS_DISABLED, refusal };
    case CreditPackPurchaseRefusal.SUBSCRIPTION_NOT_ACTIVE:
      return canOpenBillingPortal
        ? { kind: CreditCallToActionKind.BILLING_PORTAL, refusal }
        : upgrade;
    default:
      return upgrade;
  }
}
